import { supabase } from './supabaseClient';

export interface PointRule {
  id?: string;
  rule_name: string;
  description: string;
  points: number;
  is_active: boolean;
  created_at?: Date;
  updated_at?: Date;
}

export interface WelcomeBonus {
  id?: string;
  points: number;
  is_active: boolean;
  days_valid: number;
  created_at?: Date;
  updated_at?: Date;
}

class PointsServiceImpl {
  /**
   * Award points to a user with a reason
   */
  async awardPoints(userId: string, points: number, reason: string): Promise<{ success: boolean; error?: any }> {
    try {
      const { data, error } = await supabase.rpc('award_points', {
        p_user_id: userId,
        p_points: points,
        p_reason: reason
      });

      if (error) throw error;
      
      return { success: data === true };
    } catch (error) {
      console.error('Error awarding points:', error);
      return { success: false, error };
    }
  }

  /**
   * Deduct points from a user with a reason
   */
  async deductPoints(userId: string, points: number, reason: string): Promise<{ success: boolean; error?: any }> {
    try {
      const { data, error } = await supabase.rpc('deduct_points', {
        p_user_id: userId,
        p_points: points,
        p_reason: reason
      });

      if (error) throw error;
      
      return { success: data === true };
    } catch (error) {
      console.error('Error deducting points:', error);
      return { success: false, error };
    }
  }

  /**
   * Award points to a user based on a specific rule
   */
  async awardPointsForRule(userId: string, ruleId: string): Promise<{ success: boolean; error?: any }> {
    try {
      // Get the rule details
      const { data: rule, error: ruleError } = await supabase
        .from('point_rules')
        .select('*')
        .eq('id', ruleId)
        .eq('is_active', true)
        .single();
      
      if (ruleError) throw ruleError;
      if (!rule) throw new Error('Rule not found or inactive');
      
      // Award the points to the user
      const { error: pointsError } = await supabase
        .from('user_points')
        .insert({
          user_id: userId,
          points: rule.points,
          description: `Points awarded for: ${rule.rule_name}`,
          rule_id: ruleId
        });
      
      if (pointsError) throw pointsError;
      
      return { success: true };
    } catch (error) {
      console.error('Error awarding points:', error);
      return { success: false, error };
    }
  }

  /**
   * Award welcome bonus points to a new user
   */
  async awardWelcomeBonus(userId: string): Promise<{ success: boolean; error?: any }> {
    try {
      // Try using apply_point_rule first (if welcome bonus rule exists)
      try {
        const { data: ruleResult, error: ruleError } = await supabase.rpc('apply_point_rule', {
          p_user_id: userId,
          p_rule_name: 'Welcome Bonus'
        });
        
        if (!ruleError && ruleResult === true) {
          return { success: true };
        }
      } catch (ruleErr) {
        // If rule doesn't exist, fall back to settings-based approach
        console.log('Welcome Bonus rule not found, trying settings approach');
      }

      // Fallback: Get the welcome bonus settings
      const { data: bonus, error: bonusError } = await supabase
        .from('welcome_bonus_settings')
        .select('*')
        .eq('is_active', true)
        .single();
      
      if (bonusError) {
        // If no settings found, try a default welcome bonus
        if (bonusError.code === 'PGRST116') { // Not found error
          // Award default 100 points welcome bonus
          return await this.awardPoints(userId, 100, 'Welcome bonus for new user');
        }
        throw bonusError;
      }
      
      if (!bonus) {
        // Award default 100 points welcome bonus
        return await this.awardPoints(userId, 100, 'Welcome bonus for new user');
      }
      
      // Award the welcome bonus points using the new function
      return await this.awardPoints(userId, bonus.points, 'Welcome bonus for new user');
      
    } catch (error) {
      console.error('Error awarding welcome bonus:', error);
      return { success: false, error };
    }
  }

  /**
   * Get all point rules
   */
  async getPointRules(): Promise<{ data: PointRule[] | null; error: any }> {
    try {
      const { data, error } = await supabase
        .from('point_rules')
        .select('*')
        .order('created_at', { ascending: false });
      
      return { data, error };
    } catch (error) {
      console.error('Error fetching point rules:', error);
      return { data: null, error };
    }
  }

  /**
   * Get welcome bonus settings
   */
  async getWelcomeBonus(): Promise<{ data: WelcomeBonus | null; error: any }> {
    try {
      const { data, error } = await supabase
        .from('welcome_bonus_settings')
        .select('*')
        .single();
      
      return { data, error };
    } catch (error) {
      console.error('Error fetching welcome bonus settings:', error);
      return { data: null, error };
    }
  }

  /**
   * Get user's total points
   */
  async getUserPoints(userId: string): Promise<{ total: number; error?: any }> {
    try {
      // First try with RPC if available (future-proofing)
      try {
        // Match the parameter order and names exactly as defined in the function
        const { data: rpcData, error: rpcError } = await supabase
          .rpc('get_user_total_points', { 
            input_date: new Date().toISOString(),
            user_id_param: userId
          });
        
        if (!rpcError) {
          return { total: rpcData || 0 };
        }
        // If RPC fails, fall back to direct query (don't throw)
      } catch (rpcErr) {
        // Silent catch - will fall back to direct query
        console.log('RPC not available, using direct query:', rpcErr);
      }
      
      // Fallback: Get user's current points directly from users table
      const { data, error } = await supabase
        .from('users')
        .select('points')
        .eq('id', userId)
        .single();
      
      if (error) throw error;
      
      return { total: data?.points || 0 };
    } catch (error) {
      console.error('Error getting user points:', error);
      return { total: 0, error };
    }
  }

  /**
   * Award points for referring a friend
   */
  async awardReferralPoints(userId: string, referredUserId: string): Promise<{ success: boolean; error?: any }> {
    try {
      // Get the referral rule
      const { data: rule, error: ruleError } = await supabase
        .from('point_rules')
        .select('*')
        .eq('rule_name', 'Referral Bonus')
        .eq('is_active', true)
        .single();
      
      if (ruleError) {
        // If no rule found, create a default one
        if (ruleError.code === 'PGRST116') { // Not found error
          const { data: newRule, error: createError } = await supabase
            .from('point_rules')
            .insert({
              rule_name: 'Referral Bonus',
              description: 'Points awarded for referring a friend who signs up',
              points: 50, // Default value
              is_active: true
            })
            .select()
            .single();
          
          if (createError) throw createError;
          
          // Award points using the new rule
          const { error: pointsError } = await supabase
            .from('user_points')
            .insert({
              user_id: userId,
              points: newRule.points,
              description: `Referral bonus for inviting a friend`,
              rule_id: newRule.id
            });
          
          if (pointsError) throw pointsError;
          
          return { success: true };
        }
        throw ruleError;
      }
      
      // Award points using the existing rule
      const { error: pointsError } = await supabase
        .from('user_points')
        .insert({
          user_id: userId,
          points: rule.points,
          description: `Referral bonus for inviting a friend`,
          rule_id: rule.id
        });
      
      if (pointsError) throw pointsError;
      
      return { success: true };
    } catch (error) {
      console.error('Error awarding referral points:', error);
      return { success: false, error };
    }
  }

  /**
   * Award points for consecutive daily logins
   */
  async awardConsecutiveLoginPoints(userId: string, days: number): Promise<{ success: boolean; error?: any }> {
    try {
      // Use the new award_points function for streak bonuses
      const streakPoints = Math.min(days * 5, 50); // 5 points per day, max 50
      return await this.awardPoints(userId, streakPoints, `${days} day login streak bonus`);
    } catch (error) {
      console.error('Error awarding streak points:', error);
      return { success: false, error };
    }
  }

  /**
   * Get user's points history
   */
  async getPointsHistory(userId: string): Promise<{ data: any[] | null; error?: any }> {
    try {
      // Try using the RPC function first
      try {
        const { data: rpcData, error: rpcError } = await supabase.rpc('get_points_history', {
          p_user_id: userId
        });
        
        if (!rpcError) {
          return { data: rpcData || [] };
        }
      } catch (rpcErr) {
        console.log('get_points_history RPC not available, using direct query');
      }

      // Fallback: Query points_history table directly
      const { data, error } = await supabase
        .from('points_history')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(100);

      if (error) {
        // If points_history table doesn't exist, return empty array
        if (error.code === '42P01') { // undefined_table
          return { data: [] };
        }
        throw error;
      }

      return { data: data || [] };
    } catch (error) {
      console.error('Error getting points history:', error);
      return { data: [], error };
    }
  }

  /**
   * Award daily login bonus
   */
  async awardDailyLoginBonus(userId: string): Promise<{ success: boolean; error?: any }> {
    try {
      // Try using apply_point_rule first
      try {
        const { data: ruleResult, error: ruleError } = await supabase.rpc('apply_point_rule', {
          p_user_id: userId,
          p_rule_name: 'Daily Login'
        });
        
        if (!ruleError && ruleResult === true) {
          return { success: true };
        }
      } catch (ruleErr) {
        console.log('Daily Login rule not found, using default points');
      }

      // Fallback: Award default daily login bonus
      return await this.awardPoints(userId, 10, 'Daily login bonus');
    } catch (error) {
      console.error('Error awarding daily login bonus:', error);
      return { success: false, error };
    }
  }

  /**
   * Award task completion points
   */
  async awardTaskCompletionPoints(userId: string, taskPoints: number, taskTitle: string): Promise<{ success: boolean; error?: any }> {
    try {
      return await this.awardPoints(userId, taskPoints, `Task completed: ${taskTitle}`);
    } catch (error) {
      console.error('Error awarding task completion points:', error);
      return { success: false, error };
    }
  }

  /**
   * Award questionnaire completion points
   */
  async awardQuestionnairePoints(userId: string, points: number, questionnaireTitle: string): Promise<{ success: boolean; error?: any }> {
    try {
      return await this.awardPoints(userId, points, `Questionnaire completed: ${questionnaireTitle}`);
    } catch (error) {
      console.error('Error awarding questionnaire points:', error);
      return { success: false, error };
    }
  }
}

export const PointsService = new PointsServiceImpl();
export default PointsService;
